import { LogService } from '@absg/angular-common';
import {
  AccessPointDoorState, AccessPointLockState, BaseService, DoorCycle, DoorStatus, IAccessPoint, IAccessPointService, ModbusAccessPointNotification, ModbusAccessPointResponse, NimAlertService,
} from '@absg/nim-client-shared';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { SubSink } from 'subsink';

import { LockAccessService } from './lock-access.service';

@Injectable({ providedIn: 'root' })
export class AccessPointService extends BaseService implements OnDestroy, IAccessPointService {
  // The ID of the door that will be requested for unlock after the current unlocked door is closed
  queuedUnlockAccessPointId: IAccessPoint = null;
  // The ID of the door sent to the Modbus messaging service to request an unlock
  requestedUnlockAccessPointId: number = null;
  // The ID of the door that is currently unlocked
  unlockedAccessPointId: number = null;
  accessPointStateChangedNotification = new BehaviorSubject<ModbusAccessPointNotification>(null);
  unlockAccessPointResponse = new Subject<ModbusAccessPointNotification>();
  cancelUnlockAccessPointResponse = new Subject<ModbusAccessPointNotification>();
  resetAccessPointResponse = new Subject<ModbusAccessPointNotification>();
  accessPointRefresh = new Subject<boolean>();
  doorFailureResolved: Subject<number> = new Subject<number>();
  isDoorFailure: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  doorClosed: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  isResolvedDoorOpen = new BehaviorSubject<boolean>(false);
  userChooseToIgnore = new BehaviorSubject<boolean>(false);
  currentDoorProcess = new BehaviorSubject<DoorCycle>(new DoorCycle(-1, DoorStatus.Locked, true));
  isWeb = false;
  private subs = new SubSink();

  constructor(
    private nimAlertService: NimAlertService,
    private lockService: LockAccessService,
    loggingService: LogService,
  ) {
    super(loggingService);
    this.subs.sink = this.lockService.accessPointStateChangedNotification.pipe(
      tap(notification => this.onAccessPointChangedNotification(notification)),
    ).subscribe();
    this.subs.sink = this.lockService.doorRefreshIds$.pipe(
      tap(() => this.isResolvedDoorOpen.next(false)),
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.subs = null;
  }

  resetUnlockedState(activePosition: IAccessPoint): void {
    const activeAccessPointId = this.requestedUnlockAccessPointId || this.unlockedAccessPointId;
    if (activeAccessPointId) {
      // Wait to reset all state IDs until the doors are closed
      this.nimAlertService.showCloseDoorPrompt(activePosition.getAssociatedAccessPointName());
    } else {
      this.queuedUnlockAccessPointId = null;
      this.requestedUnlockAccessPointId = null;
      this.unlockedAccessPointId = null;
    }
  }

  isDoorUnlockedByPosition(position: IAccessPoint): boolean {
    if (position && this.unlockedAccessPointId) {
      return this.unlockedAccessPointId === position.getAssociatedAccessPointId();
    } else {
      return false;
    }
  }

  resetUnlockedStateByAccessPoint(accessPoint: IAccessPoint): void {
    const doorState = this.accessPointStateChangedNotification.getValue();
    if (this.userChooseToIgnore.value) {
      this.userChooseToIgnore.next(false);
      return;
    }
    // Door State not set means no door actions have been made, so door has not been touched.
    if (!doorState || doorState.doorSensorState !== AccessPointDoorState.Open) {
      this.requestedUnlockAccessPointId = null;
      this.queuedUnlockAccessPointId = null;
      this.unlockedAccessPointId = null;
      this.cancelUnlockAccessPoint(accessPoint);
    }
    // If Door is Open this will force user interaction to close, else it will finish resetting state.
    this.resetUnlockedState(accessPoint);
  }

  unlockDoorByAccessPoint(accessPoint: IAccessPoint): void {
    // Only unlock the door if it is a physical location (i.e., not Virtual or Under Hood),
    // and only allow 1 unlocked door (or 1 door in the process of unlocking) at a time
    if (this.requestedUnlockAccessPointId === null && accessPoint.isPhysicalDoor() && !accessPoint.isSimulateModeOn()) {
      let accessPointId: number;

      try {
        accessPointId = accessPoint.getAssociatedAccessPointId();
      } catch (error) {
        this.nimAlertService.showErrorAlert('Error', new Error('Door was not able to open for access point.'));
        return;
      }

      if (this.unlockedAccessPointId === null) {
        this.requestedUnlockAccessPointId = accessPointId;

        // If a door is not currently unlocked, start the unlock process; otherwise, wait until
        // the user re-closes the door to continue
        this.currentDoorProcess.next(new DoorCycle(accessPointId, DoorStatus.Unlocking));
        this.lockService.unlockAccessPoint(this.requestedUnlockAccessPointId).pipe(take(1),
          tap(response => {
            this.onUnlockAccessPointResponse(response);
          }),
        ).subscribe();
      } else if (this.unlockedAccessPointId !== accessPointId) {
        // A different door is already open, so queue the request to unlock (if it is a physical door)
        this.queuedUnlockAccessPointId = accessPoint;
      }
    } else {
      this.currentDoorProcess.next(new DoorCycle(accessPoint.getAssociatedAccessPointId(), DoorStatus.Locked, !accessPoint.isPhysicalDoor(), accessPoint.isSimulateModeOn()));
    }
  }

  onAccessPointStateChangedNotification(notification: ModbusAccessPointNotification): void {
    if (notification) {
      if (notification.isResolvedDoor && !notification.failureFlag) {
        this.currentDoorProcess.next(new DoorCycle(notification.accessPointId, DoorStatus.Failed));
        this.onDoorFailureResolved(notification.accessPointId);
      }
      if (this.isDoorClosed(notification, this.unlockedAccessPointId)) {
        this.currentDoorProcess.next(new DoorCycle(notification.accessPointId, DoorStatus.Closed));
        // If a close-door notification was shown earlier, dismiss that now because the door has been closed;
        // if not, this will have no effect and continue normally
        this.nimAlertService.dismissCloseDoorPrompt();

        // NOTE: Do not show any other notification now; closing during calculation is the normal workflow
        this.unlockedAccessPointId = null;

        // Notify listeners (e.g., Calculator) that the door was closed
        this.doorClosed.next(notification.accessPointId);

        if (this.queuedUnlockAccessPointId) {
          // If another door has been queued for unlock, pop the requested ID from the queue
          // and start the unlock process now
          const dequeuedAccessPointId = this.queuedUnlockAccessPointId;
          this.queuedUnlockAccessPointId = null;

          this.unlockDoorByAccessPoint(dequeuedAccessPointId);
        }
      } else if (this.isDoorOpenByAccessPointId(notification, this.unlockedAccessPointId)) {
        this.currentDoorProcess.next(new DoorCycle(notification.accessPointId, DoorStatus.Open));
      }
      this.accessPointStateChangedNotification.next(notification);
    }
  }

  isDoorOpen(accessPoint: IAccessPoint): boolean {
    if (!accessPoint || !accessPoint.isPhysicalDoor() || accessPoint.isSimulateModeOn()) {
      return false;
    }
    return this.isDoorUnlockedByPosition(accessPoint);
  }

  isFailedUnlock(response: ModbusAccessPointNotification, activeAccessPointId: number): boolean {
    let isFailedUnlock = false;

    if (response) {
      isFailedUnlock = response.failureFlag && activeAccessPointId === response.accessPointId;
    }

    return isFailedUnlock;
  }

  resetUnlockedAccessPointId(value: boolean): void {
    if (value === true) {
      this.unlockedAccessPointId = null;
    }
  }

  isSuccessfulUnlock(response: ModbusAccessPointNotification, activeAccessPointId: number): boolean {
    let isSuccessfulCancelUnlock = false;
    if (response) {
      isSuccessfulCancelUnlock = !response.failureFlag && activeAccessPointId === response.accessPointId;
    }
    return isSuccessfulCancelUnlock;
  }

  getDoorStatusName(doorStatus: DoorStatus): string {
    let name = '';

    // NOTE: We do an explicit conversion instead of doing an implicit conversion like
    // `name = DoorStatus[DoorStatus.Unlocked]`
    // because we do not want the strings coupled to the variable names
    // (e.g., if we renamed `DoorStatus.Closed` to `DoorStatus.ManuallyClosed`, then the UI would unintentionally
    // show "ManuallyClosed", which is not human-readable)
    switch (doorStatus) {
      case DoorStatus.Closed:
        name = 'Closed';
        break;
      case DoorStatus.Open:
        name = 'Open';
        break;
      case DoorStatus.Unlocked:
        name = 'Unlocked';
        break;
      case DoorStatus.Locked:
        name = 'Locked';
        break;
      case DoorStatus.LockedButOpen:
        name = 'Open (Locked)';
        break;
    }

    return name;
  }

  // only use by dispense door
  unlockAccessPoint(accessPointId: number, fakeUnlockOpenResponse = false, fakeClosedResponse = false): void {
    this.userChooseToIgnore.next(false);
    if (!fakeUnlockOpenResponse) {
      this.requestedUnlockAccessPointId = accessPointId;
      this.currentDoorProcess.next(new DoorCycle(accessPointId, DoorStatus.Unlocking));
      this.lockService.unlockAccessPoint(accessPointId).pipe(take(1),
        tap(response => {
          this.onUnlockAccessPointResponse(response);
        }),
        catchError(() => {
          this.retryCall();
          return of(null);
        }),
      ).subscribe();
    } else {
      this.currentDoorProcess.next(new DoorCycle(accessPointId, DoorStatus.Locked, true));
      this.sendFakeUnlockOpenClosedResponse(accessPointId, fakeClosedResponse);
    }
  }

  sendFakeUnlockOpenClosedResponse(accessPointIdLocal: number, fakeClosedResponse: boolean): void {
    // send unlock
    const response: ModbusAccessPointNotification = {
      accessPointId: accessPointIdLocal,
      failureFlag: false,
      eventId: 'fakeResponse',
    };
    this.unlockAccessPointResponse.next(response);
    // send open door
    setTimeout(() => this.sendFakeDoorStatusResponse(accessPointIdLocal, AccessPointDoorState.Open), 500);

    if (fakeClosedResponse) {
      // send closed door
      setTimeout(() => this.sendFakeDoorStatusResponse(accessPointIdLocal, AccessPointDoorState.Closed), 500);
    }
  }

  sendFakeDoorStatusResponse(accessPointIdLocal: number, doorStatus: AccessPointDoorState): void {
    const notification: ModbusAccessPointNotification = {
      accessPointId: accessPointIdLocal,
      doorSensorState: doorStatus,
    };
    this.accessPointStateChangedNotification.next(notification);
  }

  cancelUnlockAccessPoint(accessPoint: IAccessPoint): void {
    if (accessPoint.isSimulateModeOn()) { return; }
    const state = this.currentDoorProcess.value;
    const acId = accessPoint.getAssociatedAccessPointId();
    if (state.accessPointId !== acId || state.doorStatus !== DoorStatus.Unlocked) { return; }

    this.currentDoorProcess.next(new DoorCycle(this.unlockedAccessPointId, DoorStatus.Cancelled));
    this.requestedUnlockAccessPointId = null;
    this.lockService.cancelUnlockAccessPoint(acId).pipe(take(1),
      catchError((error) => {
        this.handleError(error);
        return of(null);
      }),
    ).subscribe();
  }

  isDoorClosed(notification: ModbusAccessPointNotification, activeAccessPointId: number): boolean {
    let isClosed = false;
    if (notification) {
      isClosed = notification.doorSensorState === AccessPointDoorState.Closed && activeAccessPointId === notification.accessPointId;
    }
    return isClosed;
  }

  isDoorOpenByAccessPointId(notification: ModbusAccessPointNotification, activeAccessPointId: number): boolean {
    let isOpen = false;
    if (notification) {
      isOpen = notification.doorSensorState === AccessPointDoorState.Open && activeAccessPointId === notification.accessPointId;
    }
    return isOpen;
  }

  isSuccessfulCancelUnlock(response: ModbusAccessPointNotification, activeAccessPointId: number): boolean {
    let isSuccessfulCancelUnlock = false;
    if (response) {
      isSuccessfulCancelUnlock = !response.failureFlag && activeAccessPointId === response.accessPointId;
    }
    return isSuccessfulCancelUnlock;
  }

  isFailedCancelUnlock(response: ModbusAccessPointNotification, activeAccessPointId: number): boolean {
    let isFailedCancelUnlock = false;
    if (response) {
      isFailedCancelUnlock = response.failureFlag && activeAccessPointId === response.accessPointId;
    }
    return isFailedCancelUnlock;
  }

  onCancelUnlockAccessPointResponse(response: ModbusAccessPointNotification): void {
    this.requestedUnlockAccessPointId = null;
    this.cancelUnlockAccessPointResponse.next(response);
  }

  refreshStationConfiguration(): Observable<boolean> {
    return this.lockService.refreshStationConfiguration().pipe(take(1));
  }

  onResetAccessPointResponse(response: ModbusAccessPointNotification): void {
    this.requestedUnlockAccessPointId = null;
    this.resetAccessPointResponse.next(response);
  }

  lockAllAccessPoints(): void {
    this.lockService.lockAllAccessPoints().pipe(take(1),
      catchError((error) => {
        this.handleError(error);
        return of(null);
      }),
    ).subscribe();
  }

  failAllOpenAccessPoints(): void {
    this.lockService.failAllOpenAccessPoints().pipe(take(1),
      catchError((error) => {
        this.handleError(error);
        return of(null);
      }),
    ).subscribe();
  }

  resetAllAccessPoints(): void {
    this.lockService.resetAllAccessPoints().pipe(take(1),
      catchError((error) => {
        this.handleError(error);
        return of(null);
      }),
    ).subscribe();
  }

  anyOpenDoors(): void {
    this.lockService.checkAnyOpenDoors().pipe(take(1),
      catchError((error) => {
        this.handleError(error);
        return of(null);
      }),
    ).subscribe();
  }

  getAllFailedDoor(): void {
    this.lockService.getAllFailedDoors().pipe(take(1),
      tap((results) => {
        if (results?.length > 0) {
          if (this.isDoorFailure.value) {
            this.isDoorFailure.next(false);
            this.isDoorFailure.next(true);
          } else {
            this.isDoorFailure.next(true);
          }
        }
      }),
      catchError((error) => {
        this.handleError(error);
        return of(null);
      }),
    ).subscribe();
  }

  executeShutDownDoorHandling(): void {
    this.lockService.executeCloseCalls().pipe(take(1),
      catchError((error) => {
        this.handleError(error);
        return of(null);
      }),
    ).subscribe();
  }

  requestAllAccessPointStates(): void {
    this.lockService.resetAllAccessPoints().pipe(take(1),
      catchError((error) => {
        this.handleError(error);
        return of(null);
      }),
    ).subscribe();
  }

  resetAccessPoint(accessPointId: number): void {
    this.lockService.resetAccessPoint(accessPointId).pipe(take(1),
      catchError((error) => {
        this.handleError(error);
        return of(null);
      }),
    ).subscribe();
  }

  onAccessPointChangedNotification(notification: ModbusAccessPointNotification): void {
    if (notification.failureFlag && !notification.isResolvedDoor) {
      this.isDoorFailure.next(true);
      this.requestedUnlockAccessPointId = null;
    } else {
      this.onAccessPointStateChangedNotification(notification);
    }
  }

  getFailedAccessPointIds(): Observable<number[]> {
    return this.lockService.getAllFailedDoors().pipe(take(1),
      catchError((error) => {
        this.handleError(error);
        return of([]);
      }));
  }

  private onDoorFailureResolved(accessPointId: number): void {
    this.requestedUnlockAccessPointId = null;
    this.doorFailureResolved.next(accessPointId);
    this.isDoorFailure.next(false);
  }

  private onUnlockAccessPointResponse(response: ModbusAccessPointResponse): void {
    if (response) {
      if (this.isSuccessfulUnlock(response, this.requestedUnlockAccessPointId)) {
        // NOTE: Do not show a notification now; open during calculation is the normal workflow
        this.unlockedAccessPointId = this.requestedUnlockAccessPointId;
        this.requestedUnlockAccessPointId = null;
        const notification = new ModbusAccessPointNotification();
        notification.accessPointId = response.accessPointId;
        notification.lockState = AccessPointLockState.Unlocked;
        this.accessPointStateChangedNotification.next(notification);
        this.currentDoorProcess.next(new DoorCycle(this.unlockedAccessPointId, DoorStatus.Unlocked));
      } else if (this.isFailedUnlock(response, this.requestedUnlockAccessPointId)) {
        this.currentDoorProcess.next(new DoorCycle(this.unlockedAccessPointId, DoorStatus.Failed));
        this.requestedUnlockAccessPointId = null;
      }
      this.unlockAccessPointResponse.next(response);
    } else {
      this.currentDoorProcess.next(new DoorCycle(this.requestedUnlockAccessPointId, DoorStatus.Failed));
      this.retryCall();
    }
  }

  private retryCall(): void {
    this.nimAlertService.showUnlockFailedWarning().pipe(
      take(1),
      tap((shouldRetry) => {
        if (shouldRetry) {
          const acId = this.requestedUnlockAccessPointId;
          this.requestedUnlockAccessPointId = null;
          this.unlockAccessPoint(acId);
        } else {
          this.requestedUnlockAccessPointId = null;
          this.userChooseToIgnore.next(true);
        }
      })).subscribe();
  }
}
